import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect
} from 'react';
import { Popper, TextField, ClickAwayListener } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { SimpleChip } from 'modules/common';
import { LabelValue } from 'graphql/types';

type TextFieldChipProps = {
  label?: TimesheetLabel | null;
  type: 'text' | 'number';
  onChange: (value: LabelValue | null) => void;
  handleDelete?: () => void;
  acceptSuggestion?: (value: LabelValue) => void;
};

const useSearchableStyles = makeStyles((theme) => ({
  popper: {
    boxShadow: theme.shadows[4],
    zIndex: theme.zIndex.modal + 1,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(0.5)
  },
  textField: {
    margin: theme.spacing(2, 2, 2, 2)
  },
  input: {
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield'
    }
  }
}));

export const TextFieldChip = ({
  label,
  handleDelete,
  onChange,
  type,
  acceptSuggestion
}: TextFieldChipProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState(null);
  const keyValue = useMemo(
    () => (type === 'text' ? 'stringValue' : 'numericValue'),
    [type]
  );
  const [labelValue, setLabelValue] = useState<string | null | undefined>(
    label?.value?.[keyValue]?.toString()
  );

  useEffect(() => {
    setLabelValue(label?.value?.[keyValue]?.toString());
  }, [keyValue, label?.value]);

  const open = Boolean(anchorEl);

  const chipRef = useRef<HTMLDivElement | null>(null);
  const classes = useSearchableStyles();

  const textFieldTestId = useMemo(
    () => `chip_popper_text_field_${label?.name}`,
    [label?.name]
  );

  const setValue = useCallback(() => {
    let shouldUpdate = false;
    if (label?.value?.[keyValue]) {
      if (labelValue != label?.value?.[keyValue]) {
        shouldUpdate = true;
      }
    } else {
      shouldUpdate = true;
    }
    if (shouldUpdate) {
      if (type === 'number') {
        let val = null;
        if (labelValue && !Number.isNaN(Number(labelValue))) {
          const maxAbsValue = 999999999;
          const sign = Math.sign(Number(labelValue));
          const absValue = Math.abs(Number(labelValue));
          const isValid = absValue <= maxAbsValue;
          val = sign * (isValid ? absValue : maxAbsValue);
        }
        onChange(
          val
            ? ({
                idValue: null,
                stringValue: null,
                numericValue: val
              } as LabelValue)
            : null
        );
      } else {
        let val = null;
        if (labelValue) {
          const maxCharLength = 2000;
          const charLength = labelValue.length;
          const isValid = charLength <= maxCharLength;
          val = isValid ? labelValue : labelValue.substring(0, maxCharLength);
        }
        onChange(
          val
            ? ({
                idValue: null,
                stringValue: val,
                numericValue: null
              } as LabelValue)
            : null
        );
      }
    }
  }, [keyValue, label?.value, labelValue, onChange, type]);

  const handleClick = useCallback(
    (event: any) => {
      if (anchorEl) {
        setValue();
      }
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl, setValue]
  );

  const onClickAway = useCallback(() => {
    setValue();
    setAnchorEl(null);
  }, [setValue]);

  const handleChange = useCallback((event: any) => {
    setLabelValue(event.target.value);
  }, []);

  const onHandleDelete = useCallback(() => {
    if (handleDelete) handleDelete();
    setLabelValue(null);
    setAnchorEl(null);
  }, [handleDelete]);
  return (
    <div data-testid="textfield-chip">
      <SimpleChip
        label={label}
        onChipClick={handleClick}
        handleDelete={onHandleDelete}
        disabled={false}
        chipRef={chipRef}
        acceptSuggestion={acceptSuggestion}
      />
      {open && (
        <ClickAwayListener onClickAway={onClickAway}>
          <Popper
            open={open}
            data-testid="textfield-popper"
            placement="bottom-start"
            className={classes.popper}
            anchorEl={anchorEl}
          >
            <TextField
              type={type}
              value={labelValue}
              multiline={type === 'text'}
              rows={2}
              autoFocus
              inputProps={{ 'data-testid': textFieldTestId }}
              InputLabelProps={{
                shrink: true
              }}
              onChange={handleChange}
              className={classes.textField}
              InputProps={{
                className: classes.input
              }}
            />
          </Popper>
        </ClickAwayListener>
      )}
    </div>
  );
};
